import React from 'react';
import { ShieldCheck, Globe, Zap, BadgeDollarSign, FileCheck, Lock } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import Marquee from 'react-fast-marquee';

const benefits = [
  { icon: ShieldCheck, title: "Kill Brand Theft", description: "A registered trademark gives you federal authority to shut down copycats, counterfeiters, and anyone profiting from your brand." },
  { icon: Globe, title: "50-State Exclusivity", description: "Protection isn't local. Your trademark locks down your brand name across every state in the US. No gaps. No exceptions." },
  { icon: Zap, title: "Win Disputes Faster", description: "Registered trademarks get priority in court. Stronger remedies, larger damages, and the legal teeth to enforce your rights." },
  { icon: BadgeDollarSign, title: "Increase Brand Value", description: "A trademark is an appreciating asset. It strengthens your leverage in licensing, franchising, partnerships, and acquisitions." },
  { icon: FileCheck, title: "Unlock Amazon Tools", description: "Brand Registry. A+ Content. Sponsored Brands. Automated IP protection. None of it works without a registered trademark." },
  { icon: Lock, title: "Protection That Never Expires", description: "Renew your trademark indefinitely. Once registered, your brand name is yours for life. No one can take it from you." }
];

const marqueeText = 'PROTECT YOUR BRAND \u00B7 REGISTER TODAY \u00B7 DON\'T WAIT \u00B7 FILE NOW \u00B7 ';

const BenefitCard = ({ benefit, index }) => {
  const card = useScrollAnimation({ threshold: 0.15 });
  const delay = (index % 3) + 1;

  return (
    <div
      ref={card.ref}
      data-testid={`benefit-item-${index}`}
      className={`group icon-shift relative rounded-xl p-8 bg-white/[0.02] card-hover-premium card-glow-border scroll-fade-up scroll-delay-${delay} ${card.isVisible ? 'scroll-visible' : ''}`}
    >
      <div className="icon-container w-11 h-11 rounded-lg bg-white/5 flex items-center justify-center mb-5 transition-colors">
        <benefit.icon className="w-5 h-5 text-indigo-400 transition-colors" />
      </div>
      <h3 className="text-lg font-bold tracking-tight text-white mb-2 group-hover:text-amber-400 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>{benefit.title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{benefit.description}</p>
    </div>
  );
};

const BenefitsSection = () => {
  const header = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="benefits" data-testid="benefits-section" className="py-24 md:py-32 bg-[#030308] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-12 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="text-xs tracking-[0.2em] uppercase text-amber-500 mb-3 block font-bold">Why It Matters</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-white mb-4 uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
            The Price of <span className="text-gradient-amber">Not</span> Registering
          </h2>
          <p className="text-base text-slate-300 leading-relaxed">
            Your competitors are already protected. Every day you wait is a risk you don't need to take.
          </p>
        </div>
      </div>

      {/* Dual-layer marquee ribbon */}
      <div className="relative overflow-hidden py-6 mb-16 border-y border-white/[0.03]">
        {/* Layer 1: Filled gradient text - moves right */}
        <Marquee speed={35} gradient={false} direction="right">
          {[1, 2, 3].map((_, j) => (
            <span key={j} className="text-4xl md:text-6xl font-black uppercase tracking-wider mx-6 select-none text-gradient-amber opacity-20" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {marqueeText}
            </span>
          ))}
        </Marquee>
        {/* Layer 2: Outline text - moves left, overlapping */}
        <div className="mt-3">
          <Marquee speed={25} gradient={false} direction="left">
            {[1, 2, 3].map((_, j) => (
              <span key={j} className="text-4xl md:text-6xl font-black uppercase tracking-wider mx-6 text-outline select-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {marqueeText}
              </span>
            ))}
          </Marquee>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} benefit={benefit} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
